import React, { useState, useCallback, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Box, Typography, Button, Paper, Grid, IconButton, TextField, MenuItem, Select, InputAdornment, CircularProgress, Alert, FormControl, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';
import ImageIcon from '@mui/icons-material/Image';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import StarIcon from '@mui/icons-material/Star';
import ImageAnnotationModal from './ImageAnnotationModal';
import apiService from '../services/api';
import { uploadFolder, reorderImages, uploadImagesToElevation, getBuildingPlans, renameElevation } from '../services/api';
import { hasMeaningfulDefects } from '../utils/defects';

const getDisplayFilename = (image) => {
  const raw =
    image?.filename ||
    image?.name ||
    image?.originalImage?.filename ||
    image?.originalImage?.name ||
    image?.url ||
    image?.src ||
    image?.elevation ||
    '';
  const base = String(raw).split(/[\\/]/).pop() || String(raw);
  return base.replace(/\.[^.]+$/, '') || image?.elevation || '';
};

const isSection12SitePlan = (plan) => {
  const name = String(plan?.name || '').trim().toLowerCase();
  const description = String(plan?.description || '').trim().toLowerCase();
  const isElevationView = name.startsWith('elevation view -');
  const isFacade = name.startsWith('facade') || description.includes('facade');
  const isTapping = name.startsWith('tapping inspection -') || description.includes('tapping') || description.includes('close-range');
  return !isElevationView && !isFacade && !isTapping;
};

const EditorContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  minHeight: '80vh',
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ElevationSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const ElevationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ImageCard = styled(Paper)(({ theme, selected }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  height: 150,
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
  '&.annotated': {
    border: '3px solid #4caf50',
    boxShadow: '0 0 10px rgba(76, 175, 80, 0.3)',
  },
}));

const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
  color: 'white',
  padding: theme.spacing(1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StatusIcon = styled(CheckCircleIcon)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  color: '#4caf50',
  backgroundColor: 'white',
  borderRadius: '50%',
  fontSize: '20px',
  zIndex: 2,
}));

const StatusRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  backgroundColor: '#4caf50',
  color: 'white',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 'bold',
  zIndex: 2,
}));
const CoverRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 'bold',
  zIndex: 2,
}));
const KeyFindingRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 32,
  left: 8,
  backgroundColor: '#ff9800',
  color: 'white',
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 'bold',
  zIndex: 2,
}));

const HoverOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    opacity: 1,
  },
}));

const DraggableElevationSection = ({ elevation, index, moveElevation, children }) => {
  const ref = React.useRef(null);
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ELEVATION',
    item: { index, elevationName: elevation.name },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));
  
  const [, drop] = useDrop(() => ({
    accept: 'ELEVATION',
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      
      // Call the move function
      moveElevation(dragIndex, hoverIndex);
      
      // Update the index for the dragged item
      item.index = hoverIndex;
    },
  }));
  
  drag(drop(ref));
  
  return (
    <ElevationSection 
      ref={ref} 
      sx={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
    >
      {children}
    </ElevationSection>
  );
};

const DraggableImageCard = ({ image, index, moveImage, elevationName, onEdit, onDelete, onSetAsSitePlan, buildingId, isCover = false, onSetCover = null, imageType = 'drone', isKeyFinding = false, onToggleKeyFinding = null }) => {
  const ref = React.useRef(null);
  const [hasAnnotations, setHasAnnotations] = useState(false);
  const [hasDefects, setHasDefects] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [thumbSrc, setThumbSrc] = useState('');
  const [fullSrc, setFullSrc] = useState(image?.src || '');
  const displayTitle = getDisplayFilename(image);

  // Function to generate consistent image key (matching ImageAnnotationModal format)
  const getImageKey = useCallback((img) => {
    if (!img) return null;
    const idPart =
      img.id ||
      img.file_url ||
      img.url ||
      img.src ||
      img.filename ||
      (img.originalImage && (img.originalImage.id || img.originalImage.url || img.originalImage.src)) ||
      'unknown';
    const bPart = buildingId || img.building_id || '';
    return `annotations_${String(idPart)}_${String(bPart)}`;
  }, [buildingId]);

  // Function to check if image has annotations
  const checkAnnotations = useCallback(async () => {
    const baseKey = getImageKey(image);
    if (!baseKey) {
      setHasAnnotations(false);
      setHasDefects(false);
      return;
    }

    try {
      // Backend-provided defects (from initial load)
      const backendDefects =
        hasMeaningfulDefects(image?.defects) ||
        hasMeaningfulDefects(image?.originalImage?.defects);
      
      // Check for drawing annotations (base key)
      const drawingAnnotations = localStorage.getItem(baseKey);
      let hasDrawings = false;
      if (drawingAnnotations) {
        try {
          const parsed = JSON.parse(drawingAnnotations);
          hasDrawings = Array.isArray(parsed) ? parsed.length > 0 : drawingAnnotations !== '[]';
        } catch {
          hasDrawings = drawingAnnotations !== '[]';
        }
      }
      
      // Check for annotation data (base key + _data suffix)
      const dataKey = `${baseKey}_data`;
      const annotationData = localStorage.getItem(dataKey);
      let hasLocalDefects = false;
      let hasLocalRecommendation = false;
      if (annotationData) {
        try {
          const parsed = JSON.parse(annotationData);
          hasLocalDefects = hasMeaningfulDefects(parsed?.defects);
          hasLocalRecommendation = Boolean(parsed?.recommendation && String(parsed.recommendation).trim() !== '');
        } catch {
          hasLocalDefects = true;
        }
      }

      let hasBackendDefects = Boolean(backendDefects);
      if (!hasBackendDefects && image?.id) {
        try {
          const resp = await apiService.getImageDefects(image.id);
          hasBackendDefects = hasMeaningfulDefects(resp?.defects);
        } catch (error) {
          console.error('Error checking backend defects for image:', image, error);
        }
      }
      
      // Image has annotations if backend reports any, or it has drawings/data in local storage
      const hasAnyAnnotations = Boolean(hasBackendDefects || hasDrawings || hasLocalDefects || hasLocalRecommendation);
      const hasAnyDefects = Boolean(hasBackendDefects || hasLocalDefects);
      
      // Debug logging
      console.log('🔍 Annotation Debug - Image:', image);
      console.log('🔍 Annotation Debug - Base key:', baseKey);
      console.log('🔍 Annotation Debug - Data key:', dataKey);
      console.log('🔍 Annotation Debug - Backend defects:', hasBackendDefects);
      console.log('🔍 Annotation Debug - Has drawings:', hasDrawings);
      console.log('🔍 Annotation Debug - Has local defects:', hasLocalDefects);
      console.log('🔍 Annotation Debug - Has any annotations:', hasAnyAnnotations);
      
      setHasAnnotations(hasAnyAnnotations);
      setHasDefects(hasAnyDefects);
    } catch (error) {
      console.error('Error checking annotations for image:', image, error);
      setHasAnnotations(false);
      setHasDefects(false);
    }
  }, [image, getImageKey]);

  // Check annotations on mount and when image changes
  useEffect(() => {
    checkAnnotations();

    const handleUpdated = (evt) => {
      const imageId = evt?.detail?.imageId;
      if (!imageId) return;
      if (String(imageId) !== String(image?.id)) return;
      checkAnnotations();
    };

    window.addEventListener('pfi-annotations-updated', handleUpdated);

    return () => {
      window.removeEventListener('pfi-annotations-updated', handleUpdated);
    };
  }, [checkAnnotations]);

  useEffect(() => {
    const id = image?.id;
    const src = image?.src;
    if (id && typeof src === 'string' && !src.startsWith('data:')) {
      setThumbSrc(apiService.getImageThumbUrl(id, 400, 'webp'));
      setFullSrc(apiService.getImageUrl(id));
      return;
    }
    setThumbSrc('');
    setFullSrc(src || '');
  }, [image?.id, image?.src]);
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'IMAGE',
    item: { id: image.id, index, elevationName },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));
  
  const [, drop] = useDrop(() => ({
    accept: 'IMAGE',
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      const dragElevation = item.elevationName;
      const hoverElevation = elevationName;
      
      // Don't replace items with themselves
      if (dragIndex === hoverIndex && dragElevation === hoverElevation) {
        return;
      }
      
      // Get the bounding rectangle of the hovered item
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      
      // Get horizontal middle
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      
      // Get pixels to the left
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;
      
      // Only perform the move when the mouse has crossed half of the items width
      // When dragging left, only move when the cursor is to the left of 50%
      // When dragging right, only move when the cursor is to the right of 50%
      
      // Dragging left
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }
      
      // Dragging right
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }
      
      // Call the move function
      moveImage(dragElevation, dragIndex, hoverElevation, hoverIndex, item.id);
      
      // Update the index for the dragged item
      item.index = hoverIndex;
      item.elevationName = hoverElevation;
    },
  }));
  
  drag(drop(ref));
  
  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(image, elevationName);
    }
  };
  
  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('handleDelete called - about to show confirmation');
    
    if (onDelete) {
      onDelete(image.id, elevationName);
    }
  };

  const handleSetAsSitePlan = (e) => {
    e.stopPropagation();
    if (onSetAsSitePlan) {
      onSetAsSitePlan(image, elevationName);
    }
  };
  
  const handleImageClick = (e) => {
    // Don't trigger if clicking on buttons
    if (e.target.closest('button')) {
      return;
    }
    if (onEdit) {
      onEdit(image, elevationName);
    }
  };

  return (
    <ImageCard 
      ref={ref} 
      className={hasAnnotations ? 'annotated' : ''}
      sx={{ 
        opacity: isDragging ? 0.5 : 1,
        cursor: 'pointer'
      }}
      onClick={handleImageClick}
    >
      <Box
        component="img"
        src={thumbSrc || fullSrc}
        alt={`${image.floor}`}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        onLoad={(e) => {
          const w = e.currentTarget.naturalWidth || 0;
          const h = e.currentTarget.naturalHeight || 0;
          setIsPortrait(h > w);
        }}
        onError={(e) => {
          if (thumbSrc && fullSrc && e?.currentTarget?.src !== fullSrc) {
            e.currentTarget.src = fullSrc;
          }
        }}
        sx={{ width: '100%', height: '100%', objectFit: isPortrait ? 'cover' : 'contain', display: 'block' }}
      />
      {hasAnnotations && (
        <>
          <StatusIcon />
          <StatusRibbon>ANNOTATED</StatusRibbon>
        </>
      )}
      {isCover && (
        <CoverRibbon sx={{ top: hasAnnotations ? 28 : 8 }}>ELEVATION IMAGE</CoverRibbon>
      )}
      {isKeyFinding && hasDefects && (
        <KeyFindingRibbon sx={{ top: hasAnnotations || isCover ? 52 : 8 }}>KEY FINDINGS</KeyFindingRibbon>
      )}
      <ImageOverlay>
        <Typography variant="body2" noWrap title={displayTitle}>{displayTitle}</Typography>
      </ImageOverlay>
      <HoverOverlay>
        <IconButton
          size="small"
          onClick={handleEdit}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        {onSetCover && (
          <Tooltip title={isCover ? 'Selected as Elevation Image' : 'Set as Elevation Image'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onSetCover(image); }}
              sx={{
                backgroundColor: isCover ? 'primary.light' : 'rgba(255, 255, 255, 0.9)',
                border: isCover ? '2px solid' : '1px solid',
                borderColor: isCover ? 'primary.main' : 'divider',
                color: isCover ? 'primary.contrastText' : 'inherit',
              }}
            >
              <ImageIcon fontSize="small" sx={{ color: isCover ? 'primary.main' : 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
        {onToggleKeyFinding && hasDefects && (
          <Tooltip title={isKeyFinding ? 'Marked as Key Findings' : 'Mark as Key Findings'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onToggleKeyFinding(image); }}
              sx={{
                backgroundColor: isKeyFinding ? '#ff9800' : 'rgba(255, 255, 255, 0.9)',
                border: isKeyFinding ? '2px solid #ff9800' : '1px solid',
                borderColor: isKeyFinding ? '#ff9800' : 'divider',
                color: isKeyFinding ? 'white' : 'warning.main',
                '&:hover': {
                  backgroundColor: isKeyFinding ? '#fb8c00' : 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <StarIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Set as Site Plan">
          <IconButton
            size="small"
            onClick={handleSetAsSitePlan}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: 'secondary.main',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
          >
            <MapIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </HoverOverlay>
      {image.status === 'annotated' && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CheckCircleIcon color="success" />
        </Box>
      )}
    </ImageCard>
  );
};

const AddImageCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  height: 150,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  border: `1px dashed ${theme.palette.divider}`,
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const UploadNewElevation = styled(Box)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const BulkGridEditor = ({ folderStructure, onBack, onNavigate, projectId, projectName, buildingId, buildingName, imageType }) => {
  const effectiveImageType = imageType || 'drone';
  const fetchImageType = imageType ? imageType : null;
  console.log('🚀 BulkGridEditor component rendered with folderStructure:', folderStructure, 'imageType:', effectiveImageType);
  const [resolvedBuildingName, setResolvedBuildingName] = useState(buildingName || '');
  const [elevationOrder, setElevationOrder] = useState([]);
  const [localFolderStructure, setLocalFolderStructure] = useState(folderStructure || {});
  const [annotationModalOpen, setAnnotationModalOpen] = useState(false);
  const [selectedImageForAnnotation, setSelectedImageForAnnotation] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sitePlanImageUrl, setSitePlanImageUrl] = useState('');
  const [sitePlanCandidates, setSitePlanCandidates] = useState([]);
  const [isRenamingElevation, setIsRenamingElevation] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [keyFindingsMap, setKeyFindingsMap] = useState([]);
  const elevationOrderKey = React.useMemo(() => {
    const scopeId = buildingId || projectId || 'global';
    return `elevation_order_${scopeId}`;
  }, [buildingId, projectId]);
  const elevationRenameKey = React.useMemo(() => {
    if (buildingId) return `folder_renames_building_${buildingId}`;
    if (projectId) return `folder_renames_project_${projectId}`;
    return 'folder_renames';
  }, [buildingId, projectId]);
  const resolveElevationStorageName = useCallback((elevationName) => {
    const images = localFolderStructure?.[elevationName];
    const firstImage = Array.isArray(images) ? images[0] : null;
    return (
      firstImage?.sourceElevationName ||
      firstImage?.originalElevationName ||
      firstImage?.originalImage?.elevation_name ||
      elevationName
    );
  }, [localFolderStructure]);
  const persistElevationOrder = React.useCallback((order) => {
    try {
      localStorage.setItem(elevationOrderKey, JSON.stringify(order));
    } catch {}
    // Also persist to backend when buildingId is present
    if (buildingId && Array.isArray(order) && order.length > 0) {
      try {
        apiService.saveElevationOrder(buildingId, order, effectiveImageType);
      } catch {}
    }
  }, [elevationOrderKey, buildingId, effectiveImageType]);
  
  const applyElevationRename = useCallback(async (oldName, newName) => {
    const cleanNew = (newName || '').trim();
    if (!oldName || !cleanNew || oldName === cleanNew) {
      setIsRenamingElevation(null);
      return;
    }
    // Prevent duplicate names
    const exists = Boolean(localFolderStructure[cleanNew]);
    if (exists) {
      alert('An elevation with this name already exists.');
      return;
    }
    const storageOldName = resolveElevationStorageName(oldName);
    const images = localFolderStructure[oldName] || [];
    const imageIds = images.map((img) => String(img.id || '')).filter(Boolean);
    try {
      if (projectId || buildingId) {
        await renameElevation(storageOldName, cleanNew, projectId, buildingId, effectiveImageType, imageIds);
      }
    } catch (err) {
      setError(err?.message || 'Failed to rename elevation');
      return;
    }
    // Update folder structure
    setLocalFolderStructure((prev) => {
      const updated = { ...prev };
      delete updated[oldName];
      updated[cleanNew] = images.map((img) => ({
        ...img,
        elevationName: cleanNew,
        elevation: cleanNew,
        sourceElevationName: cleanNew,
        originalElevationName: cleanNew,
        originalImage: img.originalImage ? { ...img.originalImage, elevation_name: cleanNew } : img.originalImage,
      }));
      return updated;
    });
    // Update elevation order
    setElevationOrder((prev) => {
      const copy = prev.slice();
      const idx = copy.indexOf(oldName);
      if (idx !== -1) {
        copy[idx] = cleanNew;
      } else {
        const storageIdx = copy.indexOf(storageOldName);
        if (storageIdx !== -1) copy[storageIdx] = cleanNew;
      }
      if (idx !== -1 || copy.includes(cleanNew)) {
        persistElevationOrder(copy);
      }
      return copy;
    });
    // Persist rename map
    try {
      const raw = localStorage.getItem(elevationRenameKey);
      const map = raw ? JSON.parse(raw) : {};
      map[oldName] = cleanNew;
      map[storageOldName] = cleanNew;
      localStorage.setItem(elevationRenameKey, JSON.stringify(map));
    } catch {}
    // Update site plan mapping key if present
    try {
      if (buildingId) {
        const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
        const newMap = { ...(current?.elevation_map || {}) };
        const sitePlanKey = newMap[oldName] ? oldName : (newMap[storageOldName] ? storageOldName : null);
        if (sitePlanKey) {
          newMap[cleanNew] = newMap[sitePlanKey];
          delete newMap[sitePlanKey];
          delete newMap[oldName];
          const payload = { ...current, elevation_map: newMap };
          await apiService.saveSitePlanAnnotations(buildingId, payload);
          setElevationSitePlanMap(newMap);
        } else if (newMap[cleanNew]) {
          setElevationSitePlanMap(newMap);
        }
      }
    } catch (e) {
      console.error('Failed to persist elevation rename in site plan map:', e);
    }
    setIsRenamingElevation(null);
    setRenameValue('');
  }, [localFolderStructure, elevationRenameKey, buildingId, projectId, effectiveImageType, persistElevationOrder, resolveElevationStorageName]);
  
  // Get all images in a flat array for navigation
  const getAllImages = useCallback(() => {
    const allImages = [];
    Object.entries(localFolderStructure).forEach(([elevationName, images]) => {
      if (Array.isArray(images)) {
        images.forEach((image, index) => {
          allImages.push({
            ...image,
            elevationName,
            index
          });
        });
      }
    });

    return allImages;
  }, [localFolderStructure]);

  const imageHasDefects = useCallback(async (image) => {
    const hasKnownDefects =
      hasMeaningfulDefects(image?.defects) ||
      hasMeaningfulDefects(image?.originalImage?.defects);

    if (hasKnownDefects) return true;

    if (image?.id) {
      try {
        const resp = await apiService.getImageDefects(image.id);
        if (hasMeaningfulDefects(resp?.defects)) return true;
      } catch (e) {
        console.error('Failed to check defects before marking key finding', e);
      }
    }

    try {
      const idPart =
        image?.id ||
        image?.file_url ||
        image?.url ||
        image?.src ||
        image?.filename ||
        (image?.originalImage && (image.originalImage.id || image.originalImage.url || image.originalImage.src)) ||
        null;
      if (!idPart) return false;
      const bPart = buildingId || image?.building_id || '';
      const raw = localStorage.getItem(`annotations_${String(idPart)}_${String(bPart)}_data`);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return hasMeaningfulDefects(parsed?.defects);
    } catch {
      return false;
    }
  }, [buildingId]);

  const toggleKeyFinding = useCallback(async (image) => {
    if (!buildingId || !image?.id) return;

    const id = String(image.id);
    const alreadySelected = keyFindingsMap.includes(id);

    if (!alreadySelected) {
      const hasDefects = await imageHasDefects(image);
      if (!hasDefects) {
        setError('Only images with defects can be marked as key findings.');
        return;
      }
    }

    try {
      if (alreadySelected) {
        await apiService.removeKeyFinding(buildingId, image.id, effectiveImageType);
      } else {
        await apiService.addKeyFinding(buildingId, image.id, effectiveImageType);
      }
    } catch (e) {
      console.error('Failed to update key finding', e);
      setError(e?.message || 'Failed to update key finding');
      return;
    }

    setKeyFindingsMap((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        localStorage.setItem(`key_findings_${effectiveImageType}_building_${buildingId}`, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [buildingId, effectiveImageType, imageHasDefects, keyFindingsMap]);
  
  // Handle image navigation
  const handleNavigateImage = useCallback((newIndex) => {
    const allImages = getAllImages();
    if (newIndex >= 0 && newIndex < allImages.length) {
      setCurrentImageIndex(newIndex);
      const nextImage = allImages[newIndex];
      const floorName =
        (nextImage && (nextImage.floor_name || nextImage.floorName)) ||
        (nextImage && typeof nextImage.floor === 'string' ? nextImage.floor : '') ||
        (nextImage && nextImage.originalImage && (nextImage.originalImage.floor_name || nextImage.originalImage.floorName || nextImage.originalImage.floor)) ||
        '';
      setSelectedImageForAnnotation({
        ...nextImage,
        floor_name: floorName,
        floorName: floorName,
      });
    }
  }, [getAllImages]);
  
  // Initialize elevation order when folderStructure changes
  React.useEffect(() => {
    const keys = Object.keys(folderStructure || {});
    if (keys.length === 0) {
      return;
    }
    let finalOrder = [];
    try {
      if (buildingId) {
        apiService.getElevationOrder(buildingId, effectiveImageType)
          .then((resp) => {
            const persisted = resp && Array.isArray(resp.order) ? resp.order : [];
            if (persisted.length > 0) {
              const filtered = persisted.filter((k) => keys.includes(k));
              const extras = keys.filter((k) => !filtered.includes(k));
              const merged = [...filtered, ...extras];
              setElevationOrder(merged);
              setLocalFolderStructure(folderStructure || {});
              return;
            }
            const saved = localStorage.getItem(elevationOrderKey);
            const persistedLocal = saved ? JSON.parse(saved) : null;
            if (Array.isArray(persistedLocal) && persistedLocal.length > 0) {
              const filtered = persistedLocal.filter((k) => keys.includes(k));
              const extras = keys.filter((k) => !filtered.includes(k));
              finalOrder = [...filtered, ...extras];
            } else {
              finalOrder = keys;
            }
            setElevationOrder(finalOrder);
            setLocalFolderStructure(folderStructure || {});
          })
          .catch(() => {
            const saved = localStorage.getItem(elevationOrderKey);
            const persistedLocal = saved ? JSON.parse(saved) : null;
            if (Array.isArray(persistedLocal) && persistedLocal.length > 0) {
              const filtered = persistedLocal.filter((k) => keys.includes(k));
              const extras = keys.filter((k) => !filtered.includes(k));
              finalOrder = [...filtered, ...extras];
            } else {
              finalOrder = keys;
            }
            setElevationOrder(finalOrder);
            setLocalFolderStructure(folderStructure || {});
          });
      } else {
        const saved = localStorage.getItem(elevationOrderKey);
        const persistedLocal = saved ? JSON.parse(saved) : null;
        if (Array.isArray(persistedLocal) && persistedLocal.length > 0) {
          const filtered = persistedLocal.filter((k) => keys.includes(k));
          const extras = keys.filter((k) => !filtered.includes(k));
          finalOrder = [...filtered, ...extras];
        } else {
          finalOrder = keys;
        }
        setElevationOrder(finalOrder);
        setLocalFolderStructure(folderStructure || {});
      }
    } catch {
      finalOrder = keys;
      setElevationOrder(finalOrder);
      setLocalFolderStructure(folderStructure || {});
    }
  }, [folderStructure, elevationOrderKey, buildingId, effectiveImageType]);

  React.useEffect(() => {
    if (!buildingId) {
      setKeyFindingsMap([]);
      return;
    }

    let cancelled = false;
    const loadKeyFindings = async () => {
      try {
        const resp = await apiService.listKeyFindings(buildingId, effectiveImageType);
        if (cancelled) return;
        const ids = (resp?.items || []).map((item) => String(item.image_id));
        setKeyFindingsMap(ids);
        try {
          localStorage.setItem(`key_findings_${effectiveImageType}_building_${buildingId}`, JSON.stringify(ids));
        } catch {}
      } catch (e) {
        if (cancelled) return;
        try {
          const raw = localStorage.getItem(`key_findings_${effectiveImageType}_building_${buildingId}`);
          const arr = raw ? JSON.parse(raw) : [];
          setKeyFindingsMap(Array.isArray(arr) ? arr.map(String) : []);
        } catch {
          setKeyFindingsMap([]);
        }
      }
    };

    loadKeyFindings();
    return () => {
      cancelled = true;
    };
  }, [buildingId, effectiveImageType]);

  const [elevationSitePlanMap, setElevationSitePlanMap] = useState({});
  const [sitePlanOptions, setSitePlanOptions] = useState([]);
  const [elevationCoverMap, setElevationCoverMap] = useState({});

  const fetchSitePlans = useCallback(async () => {
    if (!buildingId) {
      setSitePlanCandidates([]);
      setSitePlanOptions([]);
      setSitePlanImageUrl('');
      return;
    }
    try {
      const plans = await getBuildingPlans(buildingId);
      const relevantPlans = (plans || []).filter(isSection12SitePlan);
      const urls = [];
      const options = [];
      relevantPlans.forEach((plan) => {
        if (plan.file_url && !String(plan.file_url).match(/\.pdf$/i)) {
          const absoluteUrl = apiService.baseURL
            ? `${apiService.baseURL}${plan.file_url}`
            : plan.file_url;
          urls.push(absoluteUrl);
          options.push({
            name: plan.name || `Plan ${options.length + 1}`,
            url: absoluteUrl
          });
        }
      });
      setSitePlanCandidates(urls);
      setSitePlanOptions(options);
      
      try {
        const stored = localStorage.getItem(`site_plan_${buildingId}`);
        if (stored && urls.includes(stored)) {
          setSitePlanImageUrl(stored);
        } else if (urls.length > 0) {
          setSitePlanImageUrl(urls[0]);
        } else {
          setSitePlanImageUrl('');
        }
      } catch {
        if (urls.length > 0) {
          setSitePlanImageUrl(urls[0]);
        } else {
          setSitePlanImageUrl('');
        }
      }
    } catch {
      setSitePlanCandidates([]);
      setSitePlanOptions([]);
    }
  }, [buildingId, apiService.baseURL]);

  React.useEffect(() => {
    fetchSitePlans();
  }, [fetchSitePlans]);

  // Load elevation site plan map
  React.useEffect(() => {
    if (!buildingId) return;
    const loadMap = async () => {
      try {
        const result = await apiService.getSitePlanAnnotations(buildingId);
        if (result && result.elevation_map) {
          setElevationSitePlanMap(result.elevation_map);
        }
      } catch (e) {
        console.error('Error loading elevation map:', e);
      }
    };
    loadMap();
  }, [buildingId]);
  
  // Load elevation cover map
  React.useEffect(() => {
    if (!buildingId) return;
    const loadCover = async () => {
      try {
        const resp = await apiService.getElevationCover(buildingId, effectiveImageType);
        const mp = resp && resp.cover_by_elevation ? resp.cover_by_elevation : {};
        setElevationCoverMap(mp || {});
      } catch (e) {
        console.error('Error loading elevation cover map:', e);
        setElevationCoverMap({});
      }
    };
    loadCover();
  }, [buildingId, effectiveImageType]);

  const handleSetAsSitePlan = async (image, elevationName) => {
    if (!buildingId) return;
    
    try {
      setIsLoading(true);
      const planName = `Site Plan - ${elevationName} - ${image.floor || 'Overview'}`;
      
      const plan = await apiService.createBuildingPlanFromUrl(
        buildingId, 
        planName, 
        image.src || image.url || image.file_url,
        `Created from image in ${elevationName}`
      );
      
      await fetchSitePlans();
      
      if (plan && plan.file_url) {
        const absoluteUrl = apiService.baseURL && !plan.file_url.startsWith('http')
          ? `${apiService.baseURL}${plan.file_url}`
          : plan.file_url;
        handleLinkElevationPlan(elevationName, absoluteUrl);
      }
      
      setSuccess('Image set as site plan successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error setting site plan:', err);
      setError('Failed to set image as site plan');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSitePlanFileChange = async (e, elevationName) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      
      const file = files[0];
      try {
          setIsLoading(true);
          const plan = await apiService.uploadBuildingPlan(
              buildingId,
              file,
              `Site Plan - ${file.name}`,
              `Uploaded from elevation ${elevationName}`
          );
          await fetchSitePlans();
          
          if (plan && plan.file_url) {
            const absoluteUrl = apiService.baseURL && !plan.file_url.startsWith('http')
              ? `${apiService.baseURL}${plan.file_url}`
              : plan.file_url;
            handleLinkElevationPlan(elevationName, absoluteUrl);
          }
          
          setSuccess('Site plan uploaded successfully');
          setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
          console.error('Error uploading site plan:', err);
          setError('Failed to upload site plan');
      } finally {
          setIsLoading(false);
          e.target.value = '';
      }
  };

  const handleLinkElevationPlan = async (elevationName, url) => {
    const newMap = { ...elevationSitePlanMap, [elevationName]: url };
    setElevationSitePlanMap(newMap);
    
    try {
      // Need to fetch current annotations first to avoid overwriting by_plan_url
      // Note: In a real concurrent environment this is race-prone, but sufficient for this dashboard
      const current = await apiService.getSitePlanAnnotations(buildingId).catch(() => ({}));
      const payload = {
        ...current,
        elevation_map: newMap
      };
      await apiService.saveSitePlanAnnotations(buildingId, payload);
    } catch (e) {
      console.error('Error saving elevation map:', e);
    }
  };

  // Sync site plan when image changes
  React.useEffect(() => {
    if (selectedImageForAnnotation && elevationSitePlanMap && sitePlanCandidates.length > 0) {
      const elevName = selectedImageForAnnotation.elevationName || 
                       (selectedImageForAnnotation.originalImage && selectedImageForAnnotation.originalImage.elevation_name);
      
      if (elevName) {
        const linkedUrl = elevationSitePlanMap[elevName];
        // Only switch if the linked URL is valid and different from current
        if (linkedUrl && sitePlanCandidates.includes(linkedUrl)) {
           setSitePlanImageUrl(linkedUrl);
        }
      }
    }
  }, [selectedImageForAnnotation, elevationSitePlanMap, sitePlanCandidates]);

  const handleChangeSitePlan = (url) => {
    const next = url || '';
    setSitePlanImageUrl(next);
    try {
      if (buildingId) {
        if (next) {
          localStorage.setItem(`site_plan_${buildingId}`, next);
        } else {
          localStorage.removeItem(`site_plan_${buildingId}`);
        }
      }
    } catch {
    }
  };

  // Load folder structure from API on component mount
  const loadFolderStructure = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Prefer building-specific endpoint when buildingId is available
      let response;
      if (buildingId) {
        response = await apiService.getBuildingImages(buildingId, fetchImageType);
      } else if (projectId) {
        response = await apiService.getProjectImages(projectId);
      } else {
        response = await apiService.getFolderStructure();
      }
      
      if (response.folder_structure && Object.keys(response.folder_structure).length > 0) {
        // Convert API response to local format
        const convertedStructure = {};
        // Apply stored elevation renames if present
        let renameMap = {};
        try {
          const rawMap = localStorage.getItem(elevationRenameKey);
          renameMap = rawMap ? JSON.parse(rawMap) : {};
        } catch {}
        Object.keys(response.folder_structure).forEach(elevation => {
          const targetName = renameMap[elevation] || elevation;
          convertedStructure[targetName] = response.folder_structure[elevation].map(image => {
            const imageUrl = apiService.getImageUrl(image.id);
            console.log('🏗️ Converting image:', image.filename, 'ID:', image.id, 'URL:', imageUrl);
            const rawFloor = image.floor_name || '';
            const displayFloor = rawFloor.match(/^L(\d+)$/i)
              ? `Level ${rawFloor.replace(/^L(\d+)$/i, '$1')}`
              : (rawFloor || 'Level 1');
            return {
              id: image.id,
              src: imageUrl,
              file: null, // API images don't have file objects
              floor: displayFloor,
              filename: image.filename,
              name: image.filename,
              url: image.url,
              defects: image.defects || [],
              sourceElevationName: elevation,
              originalElevationName: elevation,
              originalImage: image
            };
          });
        });
        setLocalFolderStructure(convertedStructure);
        const keys = Object.keys(convertedStructure);
        if (buildingId) {
          try {
            const resp = await apiService.getElevationOrder(buildingId, effectiveImageType);
            const persisted = resp && Array.isArray(resp.order) ? resp.order : [];
            if (persisted.length > 0) {
              const filtered = persisted.filter((k) => keys.includes(k));
              const extras = keys.filter((k) => !filtered.includes(k));
              setElevationOrder([...filtered, ...extras]);
            } else {
              setElevationOrder(keys);
            }
          } catch {
            setElevationOrder(keys);
          }
        } else {
          setElevationOrder(keys);
        }
      }
    } catch (error) {
      console.error('Failed to load folder structure:', error);
      setError('Failed to load existing images');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, buildingId, fetchImageType, effectiveImageType]);

  React.useEffect(() => {
    loadFolderStructure();
  }, [loadFolderStructure]);
  
  const moveElevation = useCallback((dragIndex, hoverIndex) => {
    setElevationOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const draggedItem = newOrder[dragIndex];
      newOrder.splice(dragIndex, 1);
      newOrder.splice(hoverIndex, 0, draggedItem);
      persistElevationOrder(newOrder);
      return newOrder;
    });
  }, [persistElevationOrder]);
  
  const moveElevationByName = useCallback((name, delta) => {
    setElevationOrder(prev => {
      const idx = prev.indexOf(name);
      if (idx === -1) return prev;
      const newIdx = Math.max(0, Math.min(prev.length - 1, idx + delta));
      if (newIdx === idx) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      copy.splice(newIdx, 0, name);
      persistElevationOrder(copy);
      return copy;
    });
  }, [persistElevationOrder]);
  
  const moveElevationTo = useCallback((name, position) => {
    setElevationOrder(prev => {
      const idx = prev.indexOf(name);
      if (idx === -1) return prev;
      let newIdx = position === 'top' ? 0 : prev.length - 1;
      if (newIdx === idx) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      copy.splice(newIdx, 0, name);
      persistElevationOrder(copy);
      return copy;
    });
  }, [persistElevationOrder]);
  
  const handleEditImage = useCallback((image, elevationName) => {
    console.log('🔍 BULK EDITOR DEBUG - handleEditImage called with:', {
      imageId: image.id,
      imageSrc: image.src,
      imageFilename: image.filename || image.name,
      elevationName,
      fullImageObject: image
    });
    
    const allImages = getAllImages();
    const floorName =
      (image && (image.floor_name || image.floorName)) ||
      (image && typeof image.floor === 'string' ? image.floor : '') ||
      (image && image.originalImage && image.originalImage.floor_name) ||
      '';
    const imageWithElevation = { ...image, elevationName, floor_name: floorName };
    const imageIndex = allImages.findIndex(img => 
      img.id === image.id && img.elevationName === elevationName
    );
    
    console.log('🔍 BULK EDITOR DEBUG - Setting selectedImageForAnnotation:', imageWithElevation);
    
    setSelectedImageForAnnotation(imageWithElevation);
    setCurrentImageIndex(imageIndex >= 0 ? imageIndex : 0);
    setAnnotationModalOpen(true);
  }, [getAllImages]);

  useEffect(() => {
    const handleLevelUpdated = (evt) => {
      const imageId = evt?.detail?.imageId;
      const floorName = evt?.detail?.floor_name;
      if (!imageId) return;
      const nextFloor = String(floorName || '').trim();
      if (!nextFloor) return;
      setLocalFolderStructure((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.keys(next).forEach((elev) => {
          const arr = Array.isArray(next[elev]) ? next[elev] : [];
          next[elev] = arr.map((img) => {
            if (String(img?.id) !== String(imageId)) return img;
            changed = true;
            const displayFloor = nextFloor.match(/^L(\d+)$/i) ? `Level ${nextFloor.replace(/^L/i, '')}` : nextFloor;
            return {
              ...img,
              floor: displayFloor,
              floor_name: nextFloor,
              floorName: nextFloor,
              originalImage: { ...(img.originalImage || {}), floor_name: nextFloor },
            };
          });
        });
        return changed ? next : prev;
      });
      setSelectedImageForAnnotation((prev) => {
        if (!prev) return prev;
        if (String(prev?.id) !== String(imageId)) return prev;
        return { ...prev, floor_name: nextFloor };
      });
    };
    window.addEventListener('pfi-image-level-updated', handleLevelUpdated);
    return () => window.removeEventListener('pfi-image-level-updated', handleLevelUpdated);
  }, []);
  
  const handleDeleteImage = useCallback(async (imageId, elevationName) => {
    // Add debugging alert to see what parameters we're receiving
    alert(`Delete called with imageId: ${imageId}, elevationName: ${elevationName}`);
    console.log('Delete function called for:', imageId, elevationName);
    
    // Use a synchronous confirmation dialog
    const confirmed = confirm('Are you sure you want to delete this image? This action cannot be undone.');
    console.log('User confirmation result:', confirmed);
    
    if (!confirmed) {
      console.log('Delete cancelled by user');
      return;
    }

    console.log('Proceeding with deletion...');
    try {
      // Check if this is a UUID (API image) or local-style ID
      const isUUID = imageId && typeof imageId === 'string' && 
        imageId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
      
      console.log('Is UUID:', isUUID);
      
      if (isUUID) {
        // This is an API image with UUID - call the backend
        console.log('Calling API to delete image:', imageId);
        await apiService.deleteImage(imageId);
        // Reload folder structure after API deletion
        await loadFolderStructure();
      } else {
        // For local images, just remove from state
        console.log('Removing local image from state');
        setLocalFolderStructure(prevStructure => {
          const newStructure = { ...prevStructure };
          if (newStructure[elevationName]) {
            newStructure[elevationName] = newStructure[elevationName].filter(img => img.id !== imageId);
          }
          return newStructure;
        });
      }
      
      console.log('Delete completed successfully');
    } catch (error) {
      console.error('Failed to delete image:', error);
      setError(`Failed to delete image: ${error.message}`);
    }
  }, [loadFolderStructure]);
  
  const moveImage = useCallback(async (fromElevation, fromIndex, toElevation, toIndex, draggedImageId) => {
    console.log('🔄 Moving image:', { fromElevation, fromIndex, toElevation, toIndex, draggedImageId });
    
    let updatedStructure = null;
    
    // Update local state immediately for responsive UI
    setLocalFolderStructure(prevStructure => {
      const newStructure = { ...prevStructure };
      
      // Ensure both elevations exist
      if (!newStructure[fromElevation] || !newStructure[toElevation]) {
        if (!newStructure[toElevation]) {
          newStructure[toElevation] = [];
        }
        if (!newStructure[fromElevation]) {
          return prevStructure;
        }
      }
      
      // Get the image being moved - use the actual image ID instead of processed ID
      const sourceImages = newStructure[fromElevation];
      const targetImages = newStructure[toElevation];
      
      // Find the image by its actual ID
      let actualFromIndex = -1;
      for (let i = 0; i < sourceImages.length; i++) {
        if (sourceImages[i].id === draggedImageId) {
          actualFromIndex = i;
          break;
        }
      }
      
      if (actualFromIndex === -1) {
        console.warn('Could not find image with ID:', draggedImageId);
        return prevStructure;
      }
      
      const movedImage = sourceImages[actualFromIndex];
      console.log('📦 Moving image:', movedImage.filename, 'from index', actualFromIndex, 'to index', toIndex);
      
      // Remove from source
      newStructure[fromElevation] = sourceImages.filter((_, index) => index !== actualFromIndex);
      
      // Calculate proper insertion index
      let insertIndex = toIndex;
      if (fromElevation === toElevation) {
        // When moving within the same elevation, adjust for the removed item
        if (actualFromIndex < toIndex) {
          insertIndex = toIndex - 1;
        }
        insertIndex = Math.min(insertIndex, newStructure[toElevation].length);
      } else {
        insertIndex = Math.min(toIndex, targetImages.length);
      }
      
      // Insert at the new position
      newStructure[toElevation].splice(insertIndex, 0, movedImage);
      
      console.log('✅ Local state updated successfully');
      
      // Store the updated structure for database persistence
      updatedStructure = newStructure;
      
      return newStructure;
    });

    // Persist the move and reordering to the backend immediately using the updated structure
    try {
      if (updatedStructure) {
        // Persist elevation change for the dragged image
        if (draggedImageId && typeof draggedImageId === 'string' && draggedImageId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const finalIndex = Math.min(
            toIndex,
            (updatedStructure[toElevation] ? updatedStructure[toElevation].length - 1 : 0)
          );
          try {
            await apiService.moveImageElevation(draggedImageId, toElevation, finalIndex + 1, effectiveImageType);
          } catch (e) {
            console.warn('Failed to persist elevation change for image:', draggedImageId, e);
          }
        }
        const imageOrders = [];
        
        // Collect order data from the updated structure
        Object.entries(updatedStructure).forEach(([elevationName, images]) => {
          images.forEach((image, index) => {
            // Only include API images (those with UUID ids)
            if (image.id && typeof image.id === 'string' && 
                image.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
              imageOrders.push({
                id: image.id,
                order_position: index + 1 // Backend expects 1-based indexing
              });
            }
          });
        });

        // Send reorder request to backend if we have API images to reorder
        if (imageOrders.length > 0) {
          console.log('🔄 Sending reorder request to backend:', imageOrders);
          await apiService.reorderImages(imageOrders);
          console.log('✅ Images reordered successfully on backend');
          try {
            await loadFolderStructure();
          } catch {}
        } else {
          console.log('ℹ️ No API images to reorder');
        }
      }
    } catch (error) {
      console.error('❌ Failed to persist image reordering:', error);
      setError(`Failed to save image order: ${error.message}`);
    }
  }, []);
  
  const [selectedElevation, setSelectedElevation] = useState('All Elevations');
  const [selectedStatus, setSelectedStatus] = useState('All Annotation Statuses');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get project name from props or the first image
  const displayProjectName = React.useMemo(() => {
    if (projectName) {
      return projectName;
    }
    const allImages = Object.values(localFolderStructure || {}).flat();
    return allImages.length > 0 && allImages[0].projectName ? allImages[0].projectName : 'Project';
  }, [projectName, localFolderStructure]);

  // Get parent folder name from the first image's path if available
  const parentFolderName = React.useMemo(() => {
    const allImages = Object.values(localFolderStructure || {}).flat();
    if (allImages.length > 0 && allImages[0].originalImage && allImages[0].originalImage.folderPath) {
      // Extract parent folder from path like "test_elevation_data/East_Elevation/image.jpg"
      const pathParts = allImages[0].originalImage.folderPath.split('/');
      if (pathParts.length > 1) {
        return pathParts[0]; // Return the parent folder name
      }
    }
    return null;
  }, [localFolderStructure]);

  // Format title to include building name and parent folder name
  const displayTitle = React.useMemo(() => {
    let title = displayProjectName;
    if (resolvedBuildingName) {
      title = `${displayProjectName} - ${resolvedBuildingName}`;
    }
    if (parentFolderName) {
      title = `${title} - ${parentFolderName}`;
    }
    return title;
  }, [displayProjectName, resolvedBuildingName, parentFolderName]);
  
  // Process folder structure to organize by elevation
  const elevationData = React.useMemo(() => {
    const result = {};
    
    // The folder structure is already organized by elevation keys (E1, E2, etc.)
    Object.keys(localFolderStructure || {}).forEach(elevationKey => {
      if (!result[elevationKey]) {
        result[elevationKey] = [];
      }
      
      // Process images in this elevation
      localFolderStructure[elevationKey].forEach((image, index) => {
        const rawFloor =
          (image && (image.floor_name || image.floorName)) ||
          (image && image.originalImage && (image.originalImage.floor_name || image.originalImage.floorName)) ||
          (image && typeof image.floor === 'string' ? image.floor : '');
        let floor = '';
        if (rawFloor) {
          const m = String(rawFloor).trim().match(/^L(\d+)$/i);
          floor = m ? `Level ${m[1]}` : String(rawFloor).trim();
        } else if (image.file && image.file.name) {
          const floorMatch = String(image.file.name).match(/[Ll](?:evel)?[\s-]*(\d+)/) || [null, ''];
          const floorNumber = floorMatch[1];
          floor = floorNumber ? `Level ${floorNumber}` : `Level ${index + 1}`;
        } else {
          floor = `Level ${index + 1}`;
        }
        
        result[elevationKey].push({
          id: image.id || `${elevationKey}-${index}`, // Preserve original API ID or use local-style for new images
          src: image.src || 'https://via.placeholder.com/150',
          floor: floor,
          filename: image.filename || image.name || image.originalImage?.filename,
          name: image.name || image.filename || image.originalImage?.filename,
          url: image.url || image.originalImage?.url,
          status: 'pending', // Default status
          originalImage: image,
          elevationName: elevationKey
        });
      });
    });
    
    return result;
  }, [localFolderStructure]);
  
  // Convert elevationData to elevations format using the ordered elevation keys
  const elevations = elevationOrder.map(elevationName => {
    const images = elevationData[elevationName] || [];
    
    // Group images by floor
    const floorGroups = {};
    images.forEach(image => {
      if (!floorGroups[image.floor]) {
        floorGroups[image.floor] = [];
      }
      floorGroups[image.floor].push(image);
    });
    
    return {
      name: elevationName,
      floors: Object.keys(floorGroups).map(floorName => ({
        name: floorName,
        images: floorGroups[floorName]
      }))
    };
  });

  // Check if there are any images in the project
  const hasAnyImages = elevations.some(elevation => 
    elevation.floors.some(floor => floor.images.length > 0)
  );

  // Clear cache when no images exist
  useEffect(() => {
    if (!hasAnyImages && elevations.length === 0) {
      // Clear localStorage cache for this project
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('annotations_') || key.includes('_data'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('Cache cleared for empty project');
    }
  }, [hasAnyImages, elevations.length]);

  const handleElevationChange = (event) => {
    setSelectedElevation(event.target.value);
  };
  
  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  
  const handleImageUpload = async (event, elevationName) => {
    const files = Array.from(event.target.files);
    
    if (!files.length) return;
    
    try {
      setIsLoading(true);
      
      // If we have a projectId (and optionally a buildingId), upload to the backend and store in elevation folder
      if (projectId) {
        const storageElevationName = resolveElevationStorageName(elevationName);
        console.log('Uploading images to elevation:', storageElevationName, '(display:', elevationName, ') for project:', projectId, 'imageType:', effectiveImageType);
        
        const result = await uploadImagesToElevation(files, storageElevationName, projectId, buildingId || null, effectiveImageType);
        console.log('Upload result:', result);
        
        // Reload the folder structure to get the updated images
        await loadFolderStructure();
        
        setSuccess(`Successfully uploaded ${files.length} image(s) to ${elevationName}`);
      } else {
        // Fallback to local state update for projects without ID
        const timestamp = Date.now();
        const newImages = [];
        let processedCount = 0;
        
        files.forEach((file, index) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const fileName = file.name;
            const floorMatch = fileName.match(/[Ll](?:evel)?[\s-]*(\d+)/) || [null, ''];
            const floorNumber = floorMatch[1];
            const floor = floorNumber ? `Level ${floorNumber}` : `Level ${index + 1}`;
            
            const newImage = {
              id: `${elevationName}-${timestamp}-${index}`,
              src: e.target.result,
              floor: floor,
              status: 'pending',
              file: file,
              projectName: displayProjectName,
              elevation: elevationName
            };
            
            newImages.push(newImage);
            processedCount++;
            
            // Only update state when all files are processed
            if (processedCount === files.length) {
              setLocalFolderStructure(prevStructure => {
                const newStructure = { ...prevStructure };
                if (!newStructure[elevationName]) {
                  newStructure[elevationName] = [];
                }
                newStructure[elevationName] = [...newStructure[elevationName], ...newImages];
                return newStructure;
              });
            }
          };
          reader.readAsDataURL(file);
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(`Failed to upload images: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
    
    // Reset the input
    event.target.value = '';
  };
  
  const handleFolderUpload = async (event) => {
    console.log('🔥 handleFolderUpload called');
    const files = Array.from(event.target.files);
    console.log('🔥 Folder upload triggered with files:', files);
    
    if (files.length === 0) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Upload files to API with project ID
      const response = await apiService.uploadFolder(files, null, projectId, buildingId, effectiveImageType);
      console.log('Upload response:', response);
      
      // Convert API response to local format
      const convertedStructure = {};
      const uploadFolderStructure = response.folder_structure || {};
      if (!Object.keys(uploadFolderStructure).length && Array.isArray(response.images)) {
        response.images.forEach((image) => {
          const elevation = image.elevation_name || 'Unsorted';
          if (!uploadFolderStructure[elevation]) uploadFolderStructure[elevation] = [];
          uploadFolderStructure[elevation].push(image);
        });
      }
      Object.keys(uploadFolderStructure).forEach(elevation => {
        convertedStructure[elevation] = uploadFolderStructure[elevation].map(image => ({
          id: image.id,
          src: apiService.getImageUrl(image.id),
          filename: image.filename,
          name: image.filename,
          url: image.url,
          elevation: elevation,
          floor: image.floor_name || '',
          floor_name: image.floor_name || '',
          sourceElevationName: elevation,
          originalElevationName: elevation,
          defects: image.defects || [],
          originalImage: image,
          hasAnnotations: image.defects && image.defects.length > 0
        }));
      });
      
      // Update state
      setLocalFolderStructure(convertedStructure);
      const sortedKeys = Object.keys(convertedStructure).sort((a, b) => {
        // Extract numbers from folder keys if they start with digits
        const aMatch = a.match(/^(\d+)/);
        const bMatch = b.match(/^(\d+)/);
        
        if (aMatch && bMatch) {
          const numA = parseInt(aMatch[1], 10);
          const numB = parseInt(bMatch[1], 10);
          if (numA !== numB) {
            return numA - numB;
          }
          return a.localeCompare(b);
        }

        // Prioritize numeric folders over non-numeric ones
        if (aMatch && !bMatch) return -1;
        if (!aMatch && bMatch) return 1;
        
        // Check for E1, E2 format
        const aMatchE = a.match(/E(\d+)/);
        const bMatchE = b.match(/E(\d+)/);
        if (aMatchE && bMatchE) {
          return parseInt(aMatchE[1], 10) - parseInt(bMatchE[1], 10);
        }
        
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      });
      setElevationOrder(sortedKeys);
      
      console.log('Successfully uploaded and processed files');
      
    } catch (error) {
      console.error('Failed to upload folder:', error);
      setError(`Failed to upload images: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
    
    // Reset the input
    event.target.value = '';
  };
  
  const filteredElevations = selectedElevation === 'All Elevations' 
    ? elevations 
    : elevations.filter(elev => elev.name === selectedElevation);

  useEffect(() => {
    setResolvedBuildingName(buildingName || '');
  }, [buildingName, buildingId]);

  useEffect(() => {
    const run = async () => {
      if (!buildingId) return;
      if (resolvedBuildingName) return;
      try {
        const b = await apiService.getBuilding(buildingId);
        const name = b?.name || '';
        if (name) setResolvedBuildingName(name);
      } catch {}
    };
    run();
  }, [buildingId, resolvedBuildingName]);
  
  return (
    <DndProvider backend={HTML5Backend}>
      <EditorContainer>
        {/* Loading State */}
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Processing images...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success State */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ position: 'sticky', top: 0,bottom:10, zIndex: 1200, backgroundColor: 'background.default', borderBottom: 0, boxShadow: 'none', py: 1 }}>
        <HeaderContainer>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'grey.50'
            }}
          >
            <Box sx={{ minWidth: 280, flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Bulk Grid Editor</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {projectName && (
                  <Typography variant="body1" component="div" sx={{ color: 'text.primary' }}>
                    Project: <strong>{projectName}</strong>
                  </Typography>
                )}
                {resolvedBuildingName ? (
                  <Typography variant="h5" component="div" sx={{ color: 'text.primay', fontWeight: 700 }}>
                    Building: {resolvedBuildingName}
                  </Typography>
                ) : (
                  <Typography variant="subtitle1" component="div" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    (No building selected)
                  </Typography>
                )}
                {parentFolderName && (
                  <Typography variant="body2" color="text.secondary">
                    Source Folder: {parentFolderName}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<FolderIcon />}
                onClick={() => {
                  if (onNavigate) {
                    console.log('Navigating to Folder View (elevations) with:', { projectId, projectName, buildingId, buildingName: resolvedBuildingName, imageType: effectiveImageType });
                    onNavigate('elevations', { projectId, projectName, buildingId, buildingName: resolvedBuildingName, initialImageType: effectiveImageType });
                  } else if (onBack) {
                    onBack();
                  } else {
                    console.warn('No navigation handler available');
                  }
                }}
              >
                Folder View
              </Button>
              <Button variant="outlined" onClick={onBack}>Cancel</Button>
              <Button
                variant="contained"
                onClick={async () => {
                  console.log('Save Changes clicked - auto-creating Building and navigating to BuildingInfo');
                  try {
                    let targetBuildingId = buildingId;
                    if (!targetBuildingId && projectId) {
                      const buildingPayload = {
                        name: projectName ? `${projectName} Building` : 'New Building',
                        project_id: projectId,
                        description: '',
                      };
                      try {
                        const existing = await apiService.getProjectBuildings(projectId).catch(() => []);
                        if (existing && existing.length > 0) {
                          targetBuildingId = existing[0].id;
                          console.log('Using existing building:', existing[0]);
                        } else {
                          const created = await apiService.createBuilding(buildingPayload);
                          targetBuildingId = created?.id || created?.building?.id;
                          console.log('Auto-created building:', created);
                        }
                      } catch (err) {
                        console.error('Failed to resolve building for project:', err);
                      }
                    }
                    if (onNavigate) {
                      if (targetBuildingId) {
                        onNavigate('buildingInfo', { projectId, projectName, buildingId: targetBuildingId });
                      } else {
                        onNavigate('buildings', { projectId, projectName });
                      }
                    }
                  } catch (error) {
                    console.error('Error during Save Changes flow:', error);
                    if (onNavigate) {
                      onNavigate('buildings', { projectId, projectName });
                    }
                  }
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Box>

          {hasAnyImages && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'background.default'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 120, color: 'text.secondary' }}>
                Search & Filter
              </Typography>
              <TextField
                placeholder="Search images..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ minWidth: 240, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedElevation}
                  onChange={handleElevationChange}
                  displayEmpty
                >
                  <MenuItem value="All Elevations">All Elevations</MenuItem>
                  {elevations.map((elev) => (
                    <MenuItem key={elev.name} value={elev.name}>{elev.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  displayEmpty
                >
                  <MenuItem value="All Annotation Statuses">All Annotation Statuses</MenuItem>
                  <MenuItem value="Annotated">Annotated</MenuItem>
                  <MenuItem value="Not Annotated">Not Annotated</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </HeaderContainer>
        </Box>
        
        {/* Empty State */}
        {!hasAnyImages && !isLoading && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="400px"
            sx={{ 
              border: '2px dashed #ccc', 
              borderRadius: 2, 
              p: 4, 
              mt: 2,
              backgroundColor: '#fafafa'
            }}
          >
            <ImageIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Images Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              This project doesn't have any images yet. Upload some images to get started with annotations.
            </Typography>
            <Button
              component="label"
              variant="contained"
              startIcon={<UploadFileIcon />}
              size="large"
            >
              Upload Images
              <input
                type="file"
                multiple
                webkitdirectory=""
                directory=""
                style={{ display: 'none' }}
                onChange={handleFolderUpload}
              />
            </Button>
          </Box>
        )}

        {/* Show content only when there are images */}
        {hasAnyImages && (
          <>
            {filteredElevations.map((elevation, elevationIndex) => (
              <DraggableElevationSection 
                key={elevation.name} 
                elevation={elevation} 
                index={elevationIndex} 
                moveElevation={moveElevation}
              >
                <ElevationHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    {isRenamingElevation === elevation.name ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ marginRight: '8px', fontSize: '20px', cursor: 'grab' }}>⋮⋮</span>
                        <TextField
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          size="small"
                          sx={{ width: 240 }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              applyElevationRename(elevation.name, renameValue);
                            } else if (e.key === 'Escape') {
                              setIsRenamingElevation(null);
                              setRenameValue('');
                            }
                          }}
                          onBlur={() => applyElevationRename(elevation.name, renameValue)}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{ display: 'flex', alignItems: 'center' }}
                        onDoubleClick={() => {
                          setIsRenamingElevation(elevation.name);
                          setRenameValue(elevation.name);
                        }}
                      >
                        <span style={{ marginRight: '8px', fontSize: '20px', cursor: 'grab' }}>⋮⋮</span> {elevation.name}
                      </Typography>
                    )}
                    
                    {/* Link Elevation to Site Plan */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Quick move controls for whole elevation */}
                      <Tooltip title="Move elevation up">
                        <IconButton size="small" onClick={() => moveElevationByName(elevation.name, -1)} sx={{ mr: 1 }}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move elevation down">
                        <IconButton size="small" onClick={() => moveElevationByName(elevation.name, 1)} sx={{ mr: 1 }}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move elevation to top">
                        <IconButton size="small" onClick={() => moveElevationTo(elevation.name, 'top')} sx={{ mr: 1 }}>
                          <VerticalAlignTopIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move elevation to bottom">
                        <IconButton size="small" onClick={() => moveElevationTo(elevation.name, 'bottom')} sx={{ mr: 2 }}>
                          <VerticalAlignBottomIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {sitePlanOptions.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                            Link to Site Plan:
                          </Typography>
                          <FormControl size="small" variant="standard" sx={{ minWidth: 150, mr: 1 }}>
                            <Select
                              value={elevationSitePlanMap[elevation.name] || ''}
                              onChange={(e) => handleLinkElevationPlan(elevation.name, e.target.value)}
                              displayEmpty
                              disableUnderline
                              sx={{ fontSize: '0.875rem' }}
                            >
                              <MenuItem value="">
                                <em style={{ color: '#aaa' }}>None</em>
                              </MenuItem>
                              {sitePlanOptions.map((opt) => (
                                <MenuItem key={opt.url} value={opt.url}>
                                  {opt.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      )}
                      <Tooltip title="Upload new site plan for this elevation">
                        <IconButton
                          component="label"
                          size="small"
                          sx={{ color: 'primary.main', ml: 1 }}
                        >
                          <UploadFileIcon fontSize="small" />
                          <VisuallyHiddenInput 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => handleSitePlanFileChange(e, elevation.name)} 
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </ElevationHeader>
                
                <Grid container spacing={2}>
                  {(() => {
                    let globalIndex = 0;
                    return elevation.floors.map((floor) => 
                      floor.images.map((image) => {
                        const currentIndex = globalIndex++;
                        return (
                          <Grid item xs={12} sm={6} md={3} lg={2} key={image.id}>
                            <DraggableImageCard 
                              image={image}
                              index={currentIndex}
                              moveImage={moveImage}
                              elevationName={elevation.name}
                              onEdit={handleEditImage}
                              onDelete={handleDeleteImage}
                              buildingId={buildingId}
                              imageType={effectiveImageType}
                              isCover={Boolean(
                                (elevationCoverMap[elevation.name] || elevationCoverMap[resolveElevationStorageName(elevation.name)]) &&
                                String(elevationCoverMap[elevation.name] || elevationCoverMap[resolveElevationStorageName(elevation.name)]) === String(image.id)
                              )}
                              isKeyFinding={keyFindingsMap.includes(String(image.id))}
                              onToggleKeyFinding={buildingId ? toggleKeyFinding : null}
                              onSetCover={async (img) => {
                                try {
                                  if (!buildingId || !img?.id) return;
                                  const storageElevationName = resolveElevationStorageName(elevation.name);
                                  await apiService.setElevationCover(buildingId, storageElevationName, img.id, effectiveImageType);
                                  setElevationCoverMap((prev) => ({
                                    ...(prev || {}),
                                    [elevation.name]: img.id,
                                    [storageElevationName]: img.id,
                                  }));
                                } catch (e) {
                                  console.error('Failed to set elevation cover', e);
                                }
                              }}
                            />
                          </Grid>
                        );
                      })
                    ).flat();
                  })()}
                  
                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <AddImageCard component="label">
                      <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Add Image
                      </Typography>
                      <VisuallyHiddenInput 
                        type="file" 
                        accept="image/*"
                        multiple
                        onChange={(e) => handleImageUpload(e, elevation.name)} 
                      />
                    </AddImageCard>
                  </Grid>
                </Grid>
              </DraggableElevationSection>
            ))}
            
            <UploadNewElevation>
              <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" gutterBottom>Upload New Elevation</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag & drop folders here or click to select files.
              </Typography>
              <Button
                component="label"
                variant="contained"
                startIcon={<UploadFileIcon />}
              >
                Upload Elevation Folder
                <input
                  type="file"
                  multiple
                  webkitdirectory=""
                  directory=""
                  style={{ display: 'none' }}
                  onChange={handleFolderUpload}
                />
              </Button>
            </UploadNewElevation>
          </>
        )}
      </EditorContainer>
      
      <ImageAnnotationModal
        open={annotationModalOpen}
        onClose={() => {
          setAnnotationModalOpen(false);
          setSelectedImageForAnnotation(null);
        }}
        image={selectedImageForAnnotation}
        images={getAllImages()}
        currentImageIndex={currentImageIndex}
        onNavigateImage={handleNavigateImage}
        sitePlanImageUrl={sitePlanImageUrl}
        sitePlanCandidates={sitePlanCandidates}
        onChangeSitePlan={handleChangeSitePlan}
        buildingId={buildingId}
        imageType={effectiveImageType}
        showSitePlanPanel={sitePlanCandidates.length > 0}
        coverAnnotationMode={Boolean(
          selectedImageForAnnotation &&
          (elevationCoverMap[selectedImageForAnnotation.elevationName] ||
            elevationCoverMap[resolveElevationStorageName(selectedImageForAnnotation.elevationName)]) &&
          String(elevationCoverMap[selectedImageForAnnotation.elevationName] ||
            elevationCoverMap[resolveElevationStorageName(selectedImageForAnnotation.elevationName)]) === String(selectedImageForAnnotation.id)
        )}
        onSave={(annotationData) => {
          console.log('Annotation saved:', annotationData);
          // Update the image with annotation status
          if (selectedImageForAnnotation) {
            const hasAnnotations = Array.isArray(annotationData?.annotations) && annotationData.annotations.length > 0;
            setLocalFolderStructure(prev => {
              const newStructure = { ...prev };
              const elevation = newStructure[selectedImageForAnnotation.elevationName];
              if (elevation && elevation.floors) {
                elevation.floors.forEach(floor => {
                  if (floor && floor.images) {
                    const imageIndex = floor.images.findIndex(img => 
                      img.id === selectedImageForAnnotation.id || 
                      img.processedId === selectedImageForAnnotation.id
                    );
                    if (imageIndex !== -1) {
                      floor.images[imageIndex] = {
                        ...floor.images[imageIndex],
                        annotated: hasAnnotations,
                        annotations: annotationData
                      };
                    }
                  }
                });
              }
              return newStructure;
            });
          }
          // Keep the modal open for continued navigation; show success toast
          try {
            setSuccess('Annotations saved successfully');
          } catch {}
        }}
      />
    </DndProvider>
  );
};

export default BulkGridEditor;
